import { create } from "zustand";
import { stripMentionTokens } from "@/components/amy-chat/campaignMentions";
import type { ThreadLogEvent } from "@/components/amy-chat/projection";
import type { ThreadCursor } from "@/components/amy-chat/threadController";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface ConversationSummary {
  id: string;
  title: string;
  updated_at: string | null;
  shared?: boolean;
  /** True while the AI title for a just-created chat is still generating — rails render a skeleton. */
  titlePending?: boolean;
}

/** Serializable eve state persisted per thread (PUT …/eve-state). */
export interface EveThreadState {
  session?: ThreadCursor | null;
  events?: readonly ThreadLogEvent[];
}

interface ChatState {
  conversations: ConversationSummary[];
  sharedConversations: ConversationSummary[];
  conversationsLoaded: boolean;
  activeId: string | null;
  /** True when the open conversation is shared by someone else (read-only for the viewer). */
  activeReadOnly: boolean;
  /** Persisted eve session cursor of the open conversation (resume). */
  eveSession: ThreadCursor | null;
  /** Persisted eve event log of the open conversation (history render). */
  eveEvents: ThreadLogEvent[];
  /** True while a conversation's state is being fetched (drives the loading skeleton). */
  messagesLoading: boolean;
  /** First message of a just-created chat, sent by the thread component once it mounts. */
  pendingFirstMessage: string | null;
  error: string | null;

  loadConversations: (token: string, orgId: string) => Promise<void>;
  loadSharedConversations: (token: string) => Promise<void>;
  openConversation: (id: string, token: string) => Promise<void>;
  newConversation: () => void;
  createConversation: (text: string, token: string) => Promise<string | null>;
  /** Resolves false when the save was rejected (row invisible to this org/user) or failed. */
  saveEveState: (id: string, token: string, state: EveThreadState) => Promise<boolean>;
  /** AI-name a just-created chat from its first exchange (fire-and-forget; placeholder title stays on failure). */
  generateTitle: (id: string, token: string, message: string, reply: string) => Promise<void>;
  /** Manual rename (optimistic, rolls back on failure). Manual titles always beat generated ones. */
  renameConversation: (id: string, token: string, title: string) => Promise<void>;
  clearPendingFirstMessage: () => void;
  deleteConversation: (id: string, token: string) => Promise<void>;
  shareConversation: (id: string, shared: boolean, token: string) => Promise<void>;
  reset: () => void;
}

const TITLE_MAX = 60;

/** Mirror of the backend's `_title_from` so a new chat can show its title instantly. */
const titleFromMessage = (raw: string): string => {
  const text = stripMentionTokens(raw || "")
    .trim()
    .replace(/\n/g, " ");
  const truncated = text.slice(0, TITLE_MAX) + (text.length > TITLE_MAX ? "…" : "");
  return truncated || "New chat";
};

export const useChatStore = create<ChatState>((set, get) => ({
  conversations: [],
  sharedConversations: [],
  conversationsLoaded: false,
  activeId: null,
  activeReadOnly: false,
  eveSession: null,
  eveEvents: [],
  messagesLoading: false,
  pendingFirstMessage: null,
  error: null,

  loadConversations: async (token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/amy/conversations`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data?.success) {
        set((s) => ({
          // A refetch mid-generation must not drop the skeleton: carry the
          // local titlePending flag over onto the server rows.
          conversations: (data.conversations as ConversationSummary[]).map((c) => {
            const prev = s.conversations.find((p) => p.id === c.id);
            return prev?.titlePending ? { ...c, titlePending: true } : c;
          }),
        }));
      }
    } catch (e) {
      console.error("loadConversations failed", e);
    } finally {
      // Signal the first fetch has resolved so the launcher can mount + animate once.
      if (!get().conversationsLoaded) set({ conversationsLoaded: true });
    }
  },

  loadSharedConversations: async (token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/amy/conversations/shared`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data?.success) set({ sharedConversations: data.conversations });
    } catch (e) {
      console.error("loadSharedConversations failed", e);
    }
  },

  openConversation: async (id, token) => {
    // Reflect the selection immediately: mark active, clear the old thread state, and
    // show the loading skeleton while we fetch — no waiting on the round-trip.
    set({
      activeId: id,
      eveSession: null,
      eveEvents: [],
      messagesLoading: true,
      error: null,
    });
    try {
      const res = await fetch(`${API_BASE_URL}/api/amy/conversations/${id}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data?.success) {
        set({
          // A conversation opened by a non-owner (shared by a teammate) is read-only.
          activeReadOnly: data.conversation.is_owner === false,
          eveSession: data.conversation.eve_session ?? null,
          eveEvents: data.conversation.events ?? [],
          messagesLoading: false,
          error: null,
        });
      } else {
        // Clear the optimistic selection so the page never mounts a chattable
        // thread on a conversation this org/user can't read (e.g. a URL
        // carried across an org switch) — its saves would 404 silently.
        set({
          activeId: null,
          messagesLoading: false,
          error:
            data?.error === "conversation not found"
              ? "This conversation isn't available in the current organization."
              : data?.error || "Couldn't open this conversation.",
        });
      }
    } catch (e) {
      console.error("openConversation failed", e);
      set({ activeId: null, messagesLoading: false, error: "Couldn't open this conversation." });
    }
  },

  newConversation: () =>
    set({
      activeId: null,
      activeReadOnly: false,
      eveSession: null,
      eveEvents: [],
      messagesLoading: false,
      pendingFirstMessage: null,
      error: null,
    }),

  /**
   * Reserve the conversation row (rail entry + title) BEFORE the first eve
   * turn, so the chat is linkable immediately. The thread component picks up
   * `pendingFirstMessage` on mount and sends it through the eve session.
   */
  createConversation: async (text, token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/amy/conversations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text }),
      });
      const data = await res.json();
      if (!data?.success) throw new Error(data?.error || "Failed to create conversation");
      const id: string = data.conversation_id;
      set((s) => ({
        activeId: id,
        activeReadOnly: false,
        eveSession: null,
        eveEvents: [],
        pendingFirstMessage: text,
        error: null,
        conversations: [
          // titlePending drives the sidebar skeleton until the AI title lands.
          { id, title: titleFromMessage(text), updated_at: new Date().toISOString(), titlePending: true },
          ...s.conversations,
        ],
      }));
      // AI title from the first message alone, fired immediately — waiting for
      // Amy's (often long) first turn made the skeleton linger for a minute.
      void get().generateTitle(id, token, text, "");
      return id;
    } catch (e) {
      console.error("createConversation failed", e);
      set({ error: (e as Error).message });
      return null;
    }
  },

  saveEveState: async (id, token, state) => {
    try {
      const body: Record<string, unknown> = {};
      if (state.session !== undefined) body.eve_session = state.session;
      if (state.events !== undefined) body.events = state.events;
      const res = await fetch(`${API_BASE_URL}/api/amy/conversations/${id}/eve-state`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      // A 404 means the row isn't visible to the caller's org/user (e.g. a
      // thread carried across an org switch) — the transcript is NOT being
      // persisted, and the thread must say so instead of chatting into the void.
      if (!data?.success) return false;
      // Keep the rail's recency sort fresh without a full reload.
      const now = new Date().toISOString();
      set((s) => ({
        conversations: s.conversations.map((c) => (c.id === id ? { ...c, updated_at: now } : c)),
      }));
      return true;
    } catch (e) {
      console.error("saveEveState failed", e);
      return false;
    }
  },

  generateTitle: async (id, token, message, reply) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/amy/conversations/${id}/generate-title`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message, reply }),
      });
      const data = await res.json();
      if (data?.success && typeof data.title === "string" && data.title) {
        set((s) => ({
          // Apply only while still pending — a manual rename that landed
          // meanwhile cleared the flag and must win.
          conversations: s.conversations.map((c) =>
            c.id === id && c.titlePending ? { ...c, title: data.title, titlePending: false } : c
          ),
        }));
        return;
      }
    } catch (e) {
      // Non-fatal: the truncated-first-message placeholder stays.
      console.error("generateTitle failed", e);
    }
    // Failure of any kind: drop the skeleton so the placeholder title shows.
    set((s) => ({
      conversations: s.conversations.map((c) => (c.id === id ? { ...c, titlePending: false } : c)),
    }));
  },

  renameConversation: async (id, token, title) => {
    const clean = title.trim().replace(/\n/g, " ").slice(0, TITLE_MAX);
    const prev = get().conversations.find((c) => c.id === id);
    if (!clean || !prev || clean === prev.title) return;
    // Optimistic: the new name shows immediately (and kills any pending skeleton).
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === id ? { ...c, title: clean, titlePending: false } : c
      ),
    }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/amy/conversations/${id}/title`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: clean }),
      });
      const data = await res.json();
      if (!data?.success) throw new Error(data?.error || "Rename failed");
    } catch (e) {
      console.error("renameConversation failed", e);
      // Roll back to what the row had before the edit.
      set((s) => ({
        conversations: s.conversations.map((c) =>
          c.id === id ? { ...c, title: prev.title, titlePending: prev.titlePending } : c
        ),
      }));
    }
  },

  clearPendingFirstMessage: () => set({ pendingFirstMessage: null }),

  shareConversation: async (id, shared, token) => {
    // Optimistically flip the flag on the matching row.
    set((s) => ({
      conversations: s.conversations.map((c) => (c.id === id ? { ...c, shared } : c)),
    }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/amy/conversations/${id}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ shared }),
      });
      const data = await res.json();
      if (!data?.success) throw new Error(data?.error || "Failed to update sharing");
    } catch (e) {
      console.error("shareConversation failed", e);
      // Roll back on failure.
      set((s) => ({
        conversations: s.conversations.map((c) => (c.id === id ? { ...c, shared: !shared } : c)),
      }));
    }
  },

  deleteConversation: async (id, token) => {
    try {
      await fetch(`${API_BASE_URL}/api/amy/conversations/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      set((s) => ({
        conversations: s.conversations.filter((c) => c.id !== id),
        ...(s.activeId === id ? { activeId: null, eveSession: null, eveEvents: [] } : {}),
      }));
    } catch (e) {
      console.error("deleteConversation failed", e);
    }
  },

  reset: () =>
    set({
      conversations: [],
      sharedConversations: [],
      conversationsLoaded: false,
      activeId: null,
      activeReadOnly: false,
      eveSession: null,
      eveEvents: [],
      messagesLoading: false,
      pendingFirstMessage: null,
      error: null,
    }),
}));
